import React, { useEffect } from 'react';
import { Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import LandingPage from './components/LandingPage/LandingPage';
import Dashboard from './components/Dashboard/Dashboard';
import SignUpPage from './components/SignUpPage/SignUpPage';
import SignInPage from './components/SignInPage/SignInPage';
import ForgotPasswordPage from './components/SignInPage/ForgotPasswordPage';
import ClientsPage from './components/ClientsPage/ClientsPage';
import AddClientPage from './components/ClientsPage/AddClientPage';
import ClientProfilePage from './components/ClientsPage/ClientProfilePage';
import Appointments from './components/Appointments/Appointments';
import AppointmentConfirmationPage from './components/Appointments/AppointmentConfirmationPage';
import InventoryPage from './components/Inventory/InventoryPage';
import AddInventoryPage from './components/Inventory/AddInventoryPage';
import ThemeProvider from './components/ThemeContext';
import FormSelector from './components/ConsentForm/FormSelector';
import SupportPage from './components/Support/SupportPage';
import NavBar from './components/NavBar';
import SettingsPage from './components/Settings/SettingsPage';
import FAQPage from './components/FAQPage/FAQPage';
import ConsentFormsPage from './components/ConsentForm/ConsentFormsPage.js';
import SubscriptionManager from './components/Subscription/SubscriptionManager';
import BulkConsentFormsPage from './components/ConsentForm/BulkConsentFormsPage';
import LegalRouter from './components/Legal/LegalRouter';

// Auth components
import AuthGuard from './components/Auth/AuthGuard';
// We're using RoleGuardWithRoles instead of RoleGuard due to hook import issues
import RoleGuard from './components/Auth/RoleGuardWithRoles';
import Loading from './components/Common/Loading';

// Studio components
import StudioSigninHandler from './components/Auth/StudioSigninHandler';

// Subscription components
import SubscriptionPlans from './components/Subscription/SubscriptionPlans';
import SubscriptionSuccess from './components/Subscription/SubscriptionSuccess';

// Studio components
import StudioManagement from './components/Studio/StudioManagement';
import StudioInvitationAcceptance from './components/Studio/StudioInvitationAcceptance';

// Onboarding component
import OnboardingPage from './components/Onboarding/OnboardingPage';

// Record Packet components (Growth Studio)
import RecordPacketPage from './components/Appointments/RecordPacketPage';
import RecordPacketList from './components/Appointments/RecordPacketList';

// Deposit Payment components (Stripe Connect)
import DepositSuccessPage from './components/Payments/DepositSuccessPage';

// COMMENTED OUT - Public Profile components (Growth Studio) - temporarily disabled
// import ContactInquiries from './components/PublicProfiles/ContactInquiries';
// import PortfolioManager from './components/PublicProfiles/PortfolioManager';
// import PublicProfileSettings from './components/PublicProfiles/PublicProfileSettings';
// import PublicProfilePreview from './components/PublicProfiles/PublicProfilePreview';
// import PublicArtistProfile from './components/PublicProfiles/PublicArtistProfile';
// import ArtistPublicPage from './components/PublicPages/ArtistPublicPage';
// import ContactForm from './components/PublicPages/ContactForm';
// import StudioPublicPage from './components/PublicPages/StudioPublicPage';

// NEW: Inquiry Form System components (All Tiers)
import TattooInquiries from './components/PublicProfiles/TattooInquiries';
import InquiryFormSettings from './components/PublicProfiles/InquiryFormSettings';
import PublicInquiryForm from './components/PublicProfiles/PublicInquiryForm';

// Custom Form Builder (Standard+)
import CustomFormManager from './components/ConsentForm/CustomFormManager';

// ✅ NEW: Layout wrapper that handles studio auth ONCE
const AuthenticatedLayout = ({ children }) => {
  return (
    <>
      <NavBar />
      <StudioSigninHandler />
      {children}
    </>
  );
};

const AppRoutes = () => {
  const { isLoading, isAuthenticated, user } = useAuth0();
  const navigate = useNavigate();

  // Handle pending studio invitations after login
  useEffect(() => {
    if (isAuthenticated && user) {
      const pendingInvitation = sessionStorage.getItem('pendingInvitation');
      if (pendingInvitation) {
        sessionStorage.removeItem('pendingInvitation');
        // Redirect to invitation page to complete the process
        navigate(`/invitation/${pendingInvitation}`);
      }
    }
  }, [isAuthenticated, user, navigate]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/forgotpassword" element={<ForgotPasswordPage />} />
      
      {/* PUBLIC APPOINTMENT CONFIRMATION - No auth required for clients clicking from email */}
      <Route path="/appointments/:appointmentId/confirm" element={<AppointmentConfirmationPage />} />
      
      {/* PUBLIC DEPOSIT PAYMENT PAGES - No auth required (clients aren't logged in) */}
      <Route path="/deposit/success" element={<DepositSuccessPage />} />
      <Route path="/deposit/cancelled" element={<DepositSuccessPage cancelled />} />
      
      {/* COMMENTED OUT - PUBLIC ARTIST/STUDIO PROFILES - temporarily disabled
      <Route path="/artist/:artistSlug" element={<ArtistPublicPage />} />
      <Route path="/studio/:studioSlug" element={<StudioPublicPage />} />
      <Route path="/artist/:artistId" element={<PublicArtistProfile />} />
      */}
      
      {/* NEW: PUBLIC INQUIRY FORM - Shareable link for social media bios (No auth required) */}
      <Route path="/request/:type/:slug" element={<PublicInquiryForm />} />
      
      {/* Studio Invitation - Protected route with proper auth */}
      <Route path="/invitation/:token" element={
        <AuthGuard>
          <NavBar />
          <StudioInvitationAcceptance />
        </AuthGuard>
      } />
      
      {/* ✅ Legal Documents Routes - PUBLIC ACCESS */}
      <Route path="/legal/:documentType" element={<LegalRouter />} />
      
      {/* ✅ Legacy redirects for common legal document URLs */}
      <Route path="/terms-and-conditions" element={<Navigate to="/legal/terms-and-conditions" replace />} />
      <Route path="/privacy-policy" element={<Navigate to="/legal/privacy-policy" replace />} />
      <Route path="/artist-terms" element={<Navigate to="/legal/artist-terms" replace />} />
      
      {/* 🆕 ONBOARDING ROUTE - After authentication but before subscription */}
      <Route path="/onboarding" element={
        <AuthGuard>
          <NavBar />
          <OnboardingPage />
        </AuthGuard>
      } />
      
      {/* Subscription Routes */}
      <Route path="/subscription" element={
        <AuthGuard>
          <NavBar />
          <SubscriptionPlans />
        </AuthGuard>
      } />
      
      <Route path="/subscription-manager" element={
        <RoleGuard requiredRoles={['basic_subscriber', 'standard_subscriber', 'starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <NavBar />
          <SubscriptionManager />
        </RoleGuard>
      } />
      
      <Route path="/subscription/success" element={
        <AuthGuard>
          <NavBar />
          <SubscriptionSuccess />
        </AuthGuard>
      } />

      {/* ✅ FIXED: Protected Routes - Use shared AuthenticatedLayout */}
      <Route path="/dashboard" element={
        <RoleGuard requiredRoles={['basic_subscriber', 'standard_subscriber', 'starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <Dashboard />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      <Route path="/clients" element={
        <RoleGuard requiredRoles={['basic_subscriber', 'standard_subscriber', 'starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <ClientsPage />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      <Route path="/bulk-consent-forms" element={
        <RoleGuard requiredRoles={['basic_subscriber', 'standard_subscriber', 'starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <BulkConsentFormsPage />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      <Route path="/add-client" element={
        <RoleGuard requiredRoles={['basic_subscriber', 'standard_subscriber', 'starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <AddClientPage />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      <Route path="/client/:id" element={
        <RoleGuard requiredRoles={['basic_subscriber', 'standard_subscriber', 'starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <ClientProfilePage />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      {/* ✅ Client Consent Forms Page - RE-ENABLED */}
      <Route path="/client/:id/consent-forms" element={
        <RoleGuard requiredRoles={['basic_subscriber', 'standard_subscriber', 'starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <ConsentFormsPage />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      <Route path="/appointments" element={
        <RoleGuard requiredRoles={['basic_subscriber', 'standard_subscriber', 'starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <Appointments />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      {/* Record Packet Routes - Growth Studio Owners Only */}
      <Route path="/appointments/:appointmentId/record-packet" element={
        <RoleGuard requiredRoles={['growth_studio_subscriber']}>
          <AuthenticatedLayout>
            <RecordPacketPage />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      <Route path="/record-packets" element={
        <RoleGuard requiredRoles={['growth_studio_subscriber']}>
          <AuthenticatedLayout>
            <RecordPacketList />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      <Route path="/inventory" element={
        <RoleGuard requiredRoles={['basic_subscriber', 'standard_subscriber', 'starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <InventoryPage />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      <Route path="/add-inventory" element={
        <RoleGuard requiredRoles={['basic_subscriber', 'standard_subscriber', 'starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <AddInventoryPage />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      <Route path="/consent-forms" element={
        <RoleGuard requiredRoles={['basic_subscriber', 'standard_subscriber', 'starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <FormSelector />
          </AuthenticatedLayout>
        </RoleGuard>
      } />

      {/* NEW: Tattoo Inquiry Form System - ALL TIERS */}
      <Route path="/tattoo-inquiries" element={
        <RoleGuard requiredRoles={['basic_subscriber', 'standard_subscriber', 'starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <TattooInquiries />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      <Route path="/inquiry-form-settings" element={
        <RoleGuard requiredRoles={['basic_subscriber', 'standard_subscriber', 'starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <InquiryFormSettings />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      {/* Standard & Studio Features */}
      <Route path="/advanced-reports" element={
        <RoleGuard requiredRoles={['standard_subscriber', 'starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <div>Advanced Reports</div> {/* Create this component */}
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      <Route path="/custom-forms" element={
        <RoleGuard requiredRoles={['standard_subscriber', 'starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <CustomFormManager />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      {/* Studio Management - Owners and Studio Members */}
      <Route path="/studio-management" element={
        <RoleGuard requiredRoles={['starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <StudioManagement />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      {/* Team Management - Alias for Studio Management */}
      <Route path="/team-management" element={
        <RoleGuard requiredRoles={['starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <StudioManagement />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      {/* Studio Analytics - Studio plans and members */}
      <Route path="/studio-analytics" element={
        <RoleGuard requiredRoles={['starter_studio_subscriber', 'growth_studio_subscriber', 'starter_studio_member', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <div>Studio Analytics</div> {/* Create this component */}
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      {/* Growth Studio Premium Features - Growth Studio Only */}
      <Route path="/custom-integrations" element={
        <RoleGuard requiredRoles={['growth_studio_subscriber']}>
          <AuthenticatedLayout>
            <div>Custom Integrations</div> {/* Create this component */}
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      <Route path="/white-label" element={
        <RoleGuard requiredRoles={['growth_studio_subscriber']}>
          <AuthenticatedLayout>
            <div>White Label Options</div> {/* Create this component */}
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      {/* COMMENTED OUT - Public Profile Features - Growth Studio Only - temporarily disabled
      <Route path="/contact-inquiries" element={
        <RoleGuard requiredRoles={['growth_studio_subscriber', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <ContactInquiries />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      <Route path="/portfolio-manager" element={
        <RoleGuard requiredRoles={['growth_studio_subscriber', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <PortfolioManager />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      <Route path="/public-profile-settings" element={
        <RoleGuard requiredRoles={['growth_studio_subscriber', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <PublicProfileSettings />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      <Route path="/public-profile-preview" element={
        <RoleGuard requiredRoles={['growth_studio_subscriber', 'growth_studio_member']}>
          <AuthenticatedLayout>
            <PublicProfilePreview />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      */}
      
      {/* Studio Owner Only Features */}
      <Route path="/studio-billing" element={
        <RoleGuard requiredRoles={['starter_studio_subscriber', 'growth_studio_subscriber']}>
          <AuthenticatedLayout>
            <SubscriptionManager />
          </AuthenticatedLayout>
        </RoleGuard>
      } />
      
      {/* Common authenticated routes - all users */}
      <Route path="/support" element={
        <AuthGuard>
          <NavBar />
          <SupportPage />
        </AuthGuard>
      } />
      
      <Route path="/settings" element={
        <AuthGuard>
          <AuthenticatedLayout>
            <SettingsPage />
          </AuthenticatedLayout>
        </AuthGuard>
      } />
      
      <Route path="/faq" element={
        <AuthGuard>
          <NavBar />
          <FAQPage />
        </AuthGuard>
      } />
      
      {/* Catch-all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <AppRoutes />
    </ThemeProvider>
  );
};

export default App;